import { useCallback, useMemo, useState } from 'react';
import { List } from 'immutable';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';

import useAppContext from 'flow/hooks/useAppContext';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Divider from 'flow/components/ui-components/Divider';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import Queue from 'flow/types/Queue';
import { QueueKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';

import FilterSearchBar from 'flow/components/FilterSearchBar';
import SettingRow from 'flow/components/Setting/SettingRow';

interface Props {
    queuesToExclude: List<QueueKey>;
    maximumAmount: number;
    onClickAdd: (keys: List<QueueKey>) => void;
    onClose: () => void;
}

const AddAgentsModal = ({
    queuesToExclude = List(),
    maximumAmount,
    onClose,
    onClickAdd,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const queues = useAppSelector((state) => state.pbx.get('queues'));

    const [filterText, setFilterText] = useState('');
    const [queueKeysSelected, setQueueKeysSelected] =
        useState<List<QueueKey>>(List());

    const queuesToDisplayInSelectableList = useMemo(() => {
        return queues.filter(
            (queue: TypedMap<Queue>) =>
                !queuesToExclude.contains(queue.get('key')) &&
                !queueKeysSelected.contains(queue.get('key')),
        );
    }, [queues, queueKeysSelected, queuesToExclude]);

    const onClickSubmit = useCallback(() => {
        onClickAdd(queueKeysSelected);
        onClose();
    }, [dispatch, onClose, onClickAdd, queueKeysSelected]);

    const onClickSelect = useCallback(
        (key: QueueKey) => {
            setQueueKeysSelected(queueKeysSelected.push(key));
            setFilterText('');
        },
        [setQueueKeysSelected, queueKeysSelected, setFilterText],
    );

    const onClickDeselect = useCallback(
        (queueKey: QueueKey) => {
            const deselectedIndex = queueKeysSelected.findIndex(
                (key) => key === queueKey,
            );
            setQueueKeysSelected(queueKeysSelected.remove(deselectedIndex));
        },
        [queueKeysSelected, setQueueKeysSelected],
    );

    return (
        <BaseModal
            open={true}
            header={i18next.t('wallboard.hunt_group_display')}
            onClose={onClose}
            confirmButtonText={i18next.t(
                'wallboard.agents.add_users_to_dashboard',
            )}
            className="w-[600px]"
            onSubmit={onClickSubmit}
            disablePadding={true}
        >
            <div className="flex flex-col gap-3 ps-6 pe-4">
                <FilterSearchBar
                    searchText={filterText}
                    placeholder={i18next.t('input.filter')}
                    handleFilter={(event, value) => {
                        setFilterText(value);
                    }}
                />

                {!queueKeysSelected.isEmpty() ? (
                    <div className="flex flex-wrap gap-3">
                        {queueKeysSelected
                            .map((queueKey) => {
                                const queue = queues.get(queueKey);
                                return (
                                    <div
                                        key={queue.get('key')}
                                        className="flex items-center rounded-sm border border-gray-300 p-1"
                                    >
                                        <div className="px-1">
                                            {queue.get('description')}
                                        </div>
                                        <div>
                                            <IconButton
                                                aria-label={i18next.t('remove')}
                                                className="size-6"
                                                onClick={() =>
                                                    onClickDeselect(queueKey)
                                                }
                                            >
                                                <Icon iconKey="cancel" />
                                            </IconButton>
                                        </div>
                                    </div>
                                );
                            })
                            .valueSeq()}
                    </div>
                ) : null}
                <Divider />

                <div className="h-[22.5rem] overflow-y-scroll">
                    {!queuesToDisplayInSelectableList.isEmpty()
                        ? filterQueuesBySearchableInfo(
                              queuesToDisplayInSelectableList.sort(
                                  sortByDescription,
                              ),
                              filterText,
                          )
                              .map((queue: TypedMap<Queue>) => (
                                  <SettingRow
                                      key={queue.get('key')}
                                      className="h-12"
                                      cta={
                                          <IconButton
                                              aria-label={i18next.t(
                                                  'chatsettings.add',
                                              )}
                                              className="reveal-on-hover-or-focus flex"
                                              onClick={() =>
                                                  onClickSelect(
                                                      queue.get('key'),
                                                  )
                                              }
                                              disabled={
                                                  queueKeysSelected?.size ===
                                                  maximumAmount
                                              }
                                          >
                                              <Icon iconKey="add" />
                                          </IconButton>
                                      }
                                  >
                                      <div
                                          className={twMerge(
                                              'truncate',
                                              queueKeysSelected?.size ===
                                                  maximumAmount
                                                  ? 'text-gray-500'
                                                  : 'text-gray-800',
                                          )}
                                      >
                                          {queue.get('description')}
                                      </div>
                                  </SettingRow>
                              ))
                              .valueSeq()
                        : null}
                </div>
            </div>
        </BaseModal>
    );
};

const sortByDescription = (a: TypedMap<Queue>, b: TypedMap<Queue>) => {
    const aName = a?.get('description');
    const bName = b?.get('description');
    return aName < bName ? -1 : 1;
};

export const filterQueuesBySearchableInfo = (
    queues: TypedMap<Record<QueueKey, TypedMap<Queue>>>,
    filterText = '',
) => {
    const search = filterText.toLowerCase();

    return queues.filter(
        (queue: TypedMap<Queue>) =>
            queue.get('description')?.toLowerCase()?.indexOf(search) !== -1,
    );
};
export default AddAgentsModal;
